import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useAppraisalBoard, useAppraisalCycle, useAppraisalPerson } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";
import AppraisalEntry from "./Entry";

const VERDICT_CLS: Record<string, string> = {
  aligned: "bg-green-50 text-status-green",
  overrated: "bg-amber-50 text-status-amber",
  underrated: "bg-blue-50 text-brand-primary",
};

/** The review roster, and what each review actually says (HRX-08).
 *
 * The self-review gap is the output worth reading. A final score tells a manager where somebody landed; the
 * fact that they rated themselves 4.6 against their manager's 3.1 is the conversation, and it is the part that
 * never survives being averaged into one number.
 *
 * Opening a row shows the three voices, the gap, and the facts the year already recorded — attendance, hours
 * booked to projects, leave, training, certifications, promotions. None of it is scored. An appraisal that
 * turned attendance into a rating would be a worse instrument, not a better one; the point is that a manager
 * writing a review can see the year rather than remember it. */
export default function Reviews({ company }: { company: string }) {
  const { t } = useTranslation();
  const board = useAppraisalBoard(undefined, company || undefined);
  const [open, setOpen] = useState<string | null>(null);
  const cycle = useAppraisalCycle(board.data?.cycle ?? undefined);

  if (board.isError) return <div className="mt-6"><ErrorBox error={board.error} onRetry={() => void board.refetch()} /></div>;
  if (!board.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = board.data;

  if (!d.cycle) {
    return <div className="mt-6 rounded border border-brand-grey-light p-6 text-center text-sm text-brand-grey"
      data-appraisals-none>{t("apr.noCycle")}</div>;
  }

  return (
    <div className="mt-4 space-y-4" data-appraisals-reviews>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-appraisals-strip>
        <Stat label={t("apr.inCycle")} value={d.progress.appraisees} hint={String(d.cycles[0]?.cycle_name ?? "")} />
        <Stat label={t("apr.scored")} value={d.progress.scored}
          cls={d.progress.complete_pct >= 80 ? "text-status-green" : "text-status-amber"}
          hint={t("apr.scoredHint", { pct: d.progress.complete_pct })} />
        <Stat label={t("apr.notStarted")} value={d.progress.not_started}
          cls={d.progress.not_started ? "text-status-amber" : ""} hint={t("apr.notStartedHint")} />
        <Stat label={t("apr.disagreeing")} value={d.gaps.disagreeing}
          cls={d.gaps.disagreeing ? "text-status-amber" : ""}
          hint={t("apr.disagreeingHint", { over: d.gaps.overrated, under: d.gaps.underrated })} />
        <Stat label={t("apr.average")} value={d.progress.average_score.toFixed(2)} hint={t("apr.averageHint")} />
      </div>

      {d.gaps.widest && (
        <div className="rounded border border-status-amber/40 bg-amber-50 px-4 py-2 text-sm text-status-amber"
          data-appraisals-widest>
          {t("apr.widest", { who: d.gaps.widest.employee_name ?? "",
                             self: d.gaps.widest.gap.self ?? 0, manager: d.gaps.widest.gap.manager ?? 0 })}
        </div>
      )}

      <SectionCard title={t("apr.roster")} aside={t("apr.rosterN", { n: cycle.data?.rows.length ?? 0 })}>
        {!cycle.data ? <Skeleton className="m-4 h-32" /> : (
          <ul>
            {cycle.data.rows.map((r) => (
              <li key={r.name} className="border-t border-brand-grey-light first:border-t-0" data-appraisals-row={r.name}>
                <button onClick={() => setOpen(open === r.name ? null : r.name)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F6F8FA]"
                  data-appraisals-open={r.name}>
                  <div className="min-w-0">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{r.designation} · {r.company} · {r.appraisal_template}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="tabular-nums text-brand-grey">
                      {t("apr.voices", { goals: r.goal_score.toFixed(1), self: r.self_score.toFixed(1),
                                         feedback: r.avg_feedback_score ? r.avg_feedback_score.toFixed(1) : "–" })}
                    </span>
                    {r.gap.measured && (
                      <span className={`rounded px-2 py-0.5 font-semibold ${VERDICT_CLS[r.gap.verdict] ?? ""}`}
                        data-appraisals-gap={r.gap.verdict}>
                        {t(`apr.verdict.${r.gap.verdict}`)}{r.gap.gap ? ` ${r.gap.gap > 0 ? "+" : ""}${r.gap.gap}` : ""}
                      </span>
                    )}
                    <span className="rounded bg-brand-grey-light px-2 py-0.5 font-semibold text-brand-charcoal">
                      {r.blend.score ? `${r.blend.score.toFixed(2)} · ${t(`apr.band.${r.band.label}`)}` : t("apr.notStartedShort")}
                    </span>
                  </div>
                </button>
                {open === r.name && <Detail appraisal={r.name} />}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function Detail({ appraisal }: { appraisal: string }) {
  const { t } = useTranslation();
  const q = useAppraisalPerson(appraisal);
  if (q.isError) return <div className="p-4"><ErrorBox error={q.error} /></div>;
  if (!q.data) return <Skeleton className="m-4 h-32" />;
  const d = q.data;

  return (
    <div className="border-t border-brand-grey-light bg-[#F6F8FA] p-4" data-appraisals-detail={appraisal}>
      <div className="grid gap-4 lg:grid-cols-3">
        <div>
          <Head>{t("apr.managerRating")}</Head>
          {d.kras.length === 0 ? <Empty text={t("apr.notRated")} /> : d.kras.map((k, i) => (
            <Line key={i} label={k.kra ?? ""} weight={k.per_weightage} score={k.score} />
          ))}
        </div>
        <div>
          <Head>{t("apr.selfRating")}</Head>
          {d.self_ratings.length === 0 ? <Empty text={t("apr.noSelf")} /> : d.self_ratings.map((k, i) => (
            <Line key={i} label={k.criteria ?? ""} weight={k.per_weightage} score={k.score * 5} />
          ))}
        </div>
        <div>
          <Head>{t("apr.evidence")}</Head>
          <p className="mb-1 text-[11px] text-brand-grey">{t("apr.evidenceHint")}</p>
          {d.evidence.map((e) => (
            <div key={e.key} className="flex items-baseline justify-between gap-3 text-sm">
              <span>{t(`apr.fact.${e.key}`)}</span>
              <span className="font-semibold tabular-nums">
                {e.value}
                {e.detail?.late ? <span className="ml-1 text-[10px] font-normal text-status-amber">
                  {t("apr.lateN", { n: e.detail.late })}</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>

      {d.feedback.length > 0 && (
        <div className="mt-4 border-t border-brand-grey-light pt-3" data-appraisals-feedback>
          <Head>{t("apr.colleagues")}</Head>
          {d.feedback.map((f) => (
            <div key={f.name} className="mt-1 text-sm">
              <span className="font-semibold">{f.reviewer_name}</span>
              <span className="ml-2 text-[11px] text-brand-grey">{f.reviewer_designation} · {f.total_score.toFixed(1)}</span>
              <div className="text-[12px] text-brand-grey"
                dangerouslySetInnerHTML={{ __html: f.feedback ?? "" }} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-brand-grey-light pt-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("apr.blended")}</div>
          <div className="font-heading text-xl font-semibold" data-appraisals-blend>
            {d.blend.score.toFixed(2)} · {t(`apr.band.${d.band.label}`)}
          </div>
          {d.blend.reweighted && (
            <p className="mt-1 max-w-2xl text-[11px] text-brand-grey">
              {t("apr.reweighted", { missing: d.blend.missing.map((m) => t(`apr.voice.${m}`)).join(", ") })}
            </p>
          )}
        </div>
        {d.gap.measured && (
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("apr.theGap")}</div>
            <div className={`font-heading text-xl font-semibold ${d.gap.verdict === "aligned" ? "" : "text-status-amber"}`}>
              {d.gap.gap > 0 ? "+" : ""}{d.gap.gap}
            </div>
            <div className="text-[11px] text-brand-grey">{t("apr.selfVsManager", { self: d.gap.self, manager: d.gap.manager })}</div>
          </div>
        )}
      </div>
      <div className="mt-4"><AppraisalEntry appraisal={appraisal} /></div>
    </div>
  );
}

const Head = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{children}</div>
);
const Empty = ({ text }: { text: string }) => <div className="text-sm text-brand-grey">{text}</div>;

function Line({ label, weight, score }: { label: string; weight: number; score: number }) {
  return (
    <div className="mt-1 flex items-baseline justify-between gap-3 text-sm">
      <span className="min-w-0 truncate" title={label}>{label}</span>
      <span className="shrink-0 tabular-nums">
        <span className="text-[11px] text-brand-grey">{weight}%</span>{" "}
        <span className="font-semibold">{score.toFixed(1)}</span>
      </span>
    </div>
  );
}
